// PitstopPanel — "ich brauche X voraus": findet den nächsten POI einer Kategorie
// in Fahrtrichtung (nie zurück) und routet hin. Plus Test-Modus, um vor dem
// Rennen eine andere Position/Richtung vorzugaukeln.
//
// Flow: Kategorie wählen → forwardPois (lib/pitstop) über die gecachten POIs →
// Top-3 voraus → antippen → Start (aktuelle/sim Position) + Ziel (POI) an den
// Planer übergeben (pitstopTargetBus) + zu /navi/custom navigieren.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useCurrentPosition } from '../lib/useCurrentPosition';
import { useOnline } from '../lib/useOnline';
import { positionBus } from '../lib/positionBus';
import { routeBus } from '../lib/routeBus';
import {
  loadCachedPois,
  getGooglePois,
  GOOGLE_LIVE_CATEGORIES,
  POI_CATEGORIES,
  poiOpenState,
  type PoiCategoryId,
  type POI,
} from '../lib/poi';
import {
  forwardPois,
  routeBearingAt,
  compassLabel,
  PITSTOP_RADIUS_PREF,
  PITSTOP_RADIUS_DEFAULT_KM,
  type PitstopCandidate,
} from '../lib/pitstop';
import { useNumberPreference, useBoolPreference } from '../lib/usePreference';
import { useActiveRegion } from '../lib/useUiSettings';
import { pitstopTargetBus } from '../lib/pitstopTargetBus';
import { pitstopResultsBus, type PitstopResultItem } from '../lib/pitstopResultsBus';
import { computeDetour, forwardRejoin } from '../lib/pitstopDetour';
import { raceRefBus } from '../lib/raceRefBus';

const DIRS: Array<{ label: string; deg: number }> = [
  { label: 'N', deg: 0 },
  { label: 'NE', deg: 45 },
  { label: 'E', deg: 90 },
  { label: 'SE', deg: 135 },
  { label: 'S', deg: 180 },
  { label: 'SW', deg: 225 },
  { label: 'W', deg: 270 },
  { label: 'NW', deg: 315 },
];

export function PitstopPanel({ embedded = false }: { embedded?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const pos = useCurrentPosition();
  const online = useOnline();
  const navigate = useNavigate();
  const [radiusKm] = useNumberPreference(PITSTOP_RADIUS_PREF, PITSTOP_RADIUS_DEFAULT_KM);
  // „Jetzt offen"-Filter: blendet sicher geschlossene Läden aus Liste + Karte
  // aus (offene + unbekannte bleiben). Persistiert.
  const [openNowOnly, setOpenNowOnly] = useBoolPreference('adt_pitstop_open_now', false);
  // Unter-Filter „100% sicher": nur sichtbar wenn openNowOnly an. AN → nur
  // sicher GEÖFFNETE (geschlossene UND unbekannte raus). AUS → unbekannte
  // bleiben drin (nur geschlossene raus).
  const [strictOpen, setStrictOpen] = useBoolPreference('adt_pitstop_open_strict', false);
  const [activeRegion] = useActiveRegion();
  const radiusM = Math.round(radiusKm * 1000);

  const [simOn, setSimOn] = useState(positionBus.isSimulated());
  // Manuelles Richtungs-Fallback (N/NE/…). null = keins gewählt. Wird NUR
  // genutzt, wenn weder eine gefahrene Route (routeBus) noch ein GPS-Heading
  // vorliegt. Bei aktivem GPX-Ride kommt die Richtung automatisch aus der Route.
  const [manualHeading, setManualHeading] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [activeCat, setActiveCat] = useState<PoiCategoryId | null>(null);
  const [results, setResults] = useState<PitstopCandidate[] | null>(null);
  const [sourceUsed, setSourceUsed] = useState<'google' | 'osm' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSimOn(positionBus.isSimulated());
  }, [pos]);

  const hasPos = !!pos;
  const hasHeading = !!pos && typeof pos.heading === 'number' && Number.isFinite(pos.heading);

  // Effektive Fahrtrichtung: 1) entlang der gefahrenen Route (routeBus),
  // 2) sonst GPS-Heading, 3) sonst manuelles Fallback. null = unbekannt
  // (forwardPois zeigt dann alles ringsum).
  const effectiveHeading = (): { deg: number | null; src: 'route' | 'gps' | 'manual' | null } => {
    if (pos) {
      const rb = routeBearingAt(routeBus.get(), pos.latitude, pos.longitude);
      if (rb != null) return { deg: rb, src: 'route' };
      if (typeof pos.heading === 'number' && Number.isFinite(pos.heading))
        return { deg: pos.heading, src: 'gps' };
    }
    if (manualHeading != null) return { deg: manualHeading, src: 'manual' };
    return { deg: null, src: null };
  };

  const findCategory = async (
    catId: PoiCategoryId,
    openNowFlag = openNowOnly,
    strictFlag = strictOpen,
  ) => {
    setActiveCat(catId);
    setResults(null);
    setError(null);
    setSourceUsed(null);
    if (!pos) {
      setError('Keine Position. Locate-Button drücken oder Test-Modus nutzen.');
      return;
    }
    setBusy(true);
    try {
      let pois: POI[] = [];
      let usedSource: 'google' | 'osm' = 'osm';
      // Google-bevorzugte Kategorien (Kebab/Inder/Bäckerei/Supermarkt): wenn
      // online live von Google holen (frischer/korrekter). Bei Fehler oder
      // offline → OSM-Cache. Wasser/Hütten/Decathlon/Tankstelle: immer OSM.
      const tryGoogle = GOOGLE_LIVE_CATEGORIES.has(catId);
      if (tryGoogle) {
        try {
          // cache-first (auch offline), sonst Live-Call wenn online.
          pois = await getGooglePois(catId, pos.latitude, pos.longitude, online, radiusM);
          usedSource = 'google';
        } catch (e) {
          console.warn('[pitstop] Google nicht verfügbar, fallback OSM', e);
        }
      }
      if (!pois.length) {
        pois = await loadCachedPois(catId);
        usedSource = 'osm';
      }
      if (!pois.length) {
        setError(
          tryGoogle
            ? 'Nichts von Google und nicht offline geladen — unter Offline-Downloads laden.'
            : 'Diese Kategorie ist nicht offline geladen — unter Offline-Downloads laden.',
        );
        setResults([]);
        return;
      }
      const eff = effectiveHeading();
      const route = routeBus.get();
      // Mit aktiver Route: enger Kegel um die Routenrichtung, mehr Kandidaten,
      // dann echten Straßen-Umweg berechnen und nach geringstem Umweg ranken.
      // Ohne Route: weiter Kegel (160° → nur was DIREKT hinter dir liegt fällt
      // raus), nach Nähe — sonst schickt dich eine grobe Handrichtung an nahen
      // Treffern vorbei zu fernen. Mehr Treffer anzeigen (bis 5).
      const candidatesAll = forwardPois(
        { lat: pos.latitude, lon: pos.longitude, heading: eff.deg },
        pois,
        { limit: route ? 3 : 5, maxConeDeg: route ? 100 : 160, maxDistKm: radiusKm },
      );
      // „Jetzt offen"-Filter: greift auf Liste UND Kartenmarker.
      //   openNowFlag aus → kein Filter.
      //   openNowFlag an, strict aus → nur GESCHLOSSENE raus (unbekannte bleiben).
      //   openNowFlag an, strict an → nur sicher GEÖFFNETE (unbekannte auch raus).
      const candidates = !openNowFlag
        ? candidatesAll
        : candidatesAll.filter((c) => {
            const st = poiOpenState(c.poi);
            return strictFlag ? st === 'open' : st !== 'closed';
          });
      setSourceUsed(usedSource);
      setResults(candidates.slice(0, 3));

      let items: PitstopResultItem[] = candidates.map((c) => ({
        id: c.poi.id,
        lat: c.poi.lat,
        lon: c.poi.lon,
        name: c.poi.name || POI_CATEGORIES[catId].label,
        category: catId,
        distM: c.distM,
        detourKm: null,
        detourHm: null,
      }));

      if (route) {
        const detours = await Promise.all(
          candidates.map((c) =>
            computeDetour(route, pos.latitude, pos.longitude, c.poi.lat, c.poi.lon, activeRegion),
          ),
        );
        items = items.map((it, i) => ({
          ...it,
          detourKm: detours[i]?.detourKm ?? null,
          detourHm: detours[i]?.detourHm ?? null,
          exit: detours[i]?.exit ?? null,
          rejoin: detours[i]?.rejoin ?? null,
        }));
        // Geringster Umweg zuerst; nicht berechenbare ans Ende.
        items.sort(
          (a, b) => (a.detourKm ?? Infinity) - (b.detourKm ?? Infinity),
        );
      }
      // Mit Route die 3 umweg-ärmsten; ohne Route die 5 nächsten zeigen.
      items = items.slice(0, route ? 3 : 5);

      if (items.length) {
        pitstopResultsBus.publish({
          origin: { lat: pos.latitude, lon: pos.longitude },
          items,
        });
        setOpen(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Toggle „Jetzt offen": Status merken UND die aktuelle Suche sofort neu
  // ausführen (mit dem neuen Wert, da der State erst beim nächsten Render steht),
  // damit Liste + Kartenmarker direkt aktualisiert werden.
  const toggleOpenNow = () => {
    const next = !openNowOnly;
    setOpenNowOnly(next);
    if (activeCat) void findCategory(activeCat, next, strictOpen);
  };
  const toggleStrict = () => {
    const next = !strictOpen;
    setStrictOpen(next);
    // strict ist nur sichtbar wenn openNowOnly an → mit true neu suchen.
    if (activeCat) void findCategory(activeCat, true, next);
  };

  const routeTo = (c: PitstopCandidate) => {
    if (!pos) return;
    const start: [number, number] = [pos.longitude, pos.latitude];
    const endName = c.poi.name || (activeCat ? POI_CATEGORIES[activeCat].label : 'Ziel');
    // ULTRA: Route zum POI UND zurück auf die Originalstrecke. Strecke aus
    // routeBus (aktiv gefolgte) oder raceRefBus (persistente Referenz). Rejoin =
    // nächstgelegener Punkt VORNE auf der Strecke (kurzer Schlenker, kein Skip).
    const route = routeBus.get() ?? raceRefBus.get();
    if (route) raceRefBus.set(route);
    const rejoin = route
      ? forwardRejoin(route, pos.latitude, pos.longitude, c.poi.lat, c.poi.lon)
      : null;
    pitstopTargetBus.publish(
      route && rejoin
        ? {
            start,
            via: [[c.poi.lon, c.poi.lat]] as Array<[number, number]>,
            end: rejoin,
            endName,
            raceRoute: route,
          }
        : { start, end: [c.poi.lon, c.poi.lat], endName },
    );
    setOpen(false);
    navigate('/navi/custom');
  };

  const triggerClass = embedded
    ? 'relative h-[22px] px-1.5 rounded-md flex items-center gap-1 text-white/80 hover:text-white text-[11px] font-semibold leading-none tracking-wide transition-colors'
    : 'relative h-[26px] px-2 rounded-md shadow-sm flex items-center gap-1 border border-white/10 bg-black/60 hover:bg-black/75 active:bg-black/85 transition-colors text-white text-[12px] font-medium leading-none';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={triggerClass}
        aria-label="Pit-stop ahead"
        title="Nächster Stopp voraus"
      >
        <span aria-hidden="true">🍽️</span>
        <span>Voraus</span>
        {simOn && (
          <span
            className="absolute -top-1 -right-1.5 min-w-[14px] h-[14px] px-1 rounded-full bg-fuchsia-500 text-white text-[9px] font-bold flex items-center justify-center leading-none"
            title="Test-Modus aktiv"
          >
            T
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center px-4"
            role="dialog"
            aria-modal="true"
            aria-label="Pit-stop voraus"
            onClick={() => setOpen(false)}
            style={{
              paddingTop: 'max(1rem, env(safe-area-inset-top))',
              paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
            }}
          >
            <div
              className="bg-turbo-900 border border-turbo-500/30 rounded-2xl p-4 w-full max-w-[320px] shadow-xl max-h-full overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-semibold flex-1">Nächster Stopp voraus</h3>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 text-xl leading-none"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              {/* Position-Status. Test-Standort wird im Profil (Admin) aktiviert
                  und per Long-Press auf die Karte gesetzt. */}
              <div className="text-[12px] text-white/70 mb-2">
                {hasPos ? (
                  <span>
                    {simOn ? '🧪 Test-Position' : '📍 GPS'}: {pos!.latitude.toFixed(4)}, {pos!.longitude.toFixed(4)}
                  </span>
                ) : (
                  <span className="text-amber-300">
                    Keine Position — Locate drücken (oder im Profil den Test-Standort aktivieren).
                  </span>
                )}
              </div>

              {/* Fahrtrichtung. Bei aktivem GPX-Ride automatisch aus der Route;
                  sonst GPS-Heading; die Knöpfe sind nur ein manuelles Fallback. */}
              <div className="rounded-lg border border-white/10 bg-white/5 p-2.5 mb-3">
                <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1.5">
                  Fahrtrichtung
                </div>
                <div className="text-[12px] text-white/70 mb-2">
                  {routeBus.get()
                    ? '🧭 Automatisch aus gefahrener Route'
                    : hasHeading
                      ? `🧭 Aus GPS (${compassLabel(pos!.heading as number)})`
                      : manualHeading != null
                        ? `🧭 Manuell: ${compassLabel(manualHeading)}`
                        : 'Keine Route/GPS — Richtung wählen (sonst werden alle Treffer ringsum gezeigt).'}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {DIRS.map((d) => (
                    <button
                      key={d.deg}
                      type="button"
                      onClick={() => setManualHeading(d.deg)}
                      className={`px-2 py-1 rounded text-[12px] border ${
                        manualHeading === d.deg
                          ? 'bg-fuchsia-500/40 border-fuchsia-300 text-white'
                          : 'bg-turbo-800/60 border-turbo-700/50 text-white/70'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                  {manualHeading != null && (
                    <button
                      type="button"
                      onClick={() => setManualHeading(null)}
                      className="px-2 py-1 rounded text-[12px] border bg-white/10 border-white/20 text-white/70"
                      aria-label="Manuelle Richtung löschen"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* „Jetzt offen"-Filter — blendet sicher geschlossene Läden aus
                  Liste UND Karte aus (offene + unbekannte bleiben). */}
              <button
                type="button"
                onClick={toggleOpenNow}
                role="switch"
                aria-checked={openNowOnly}
                className={`mb-3 w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 ${
                  openNowOnly
                    ? 'bg-emerald-600/25 border-emerald-400/60'
                    : 'bg-turbo-800/60 border-turbo-700/50'
                }`}
              >
                <span className="flex items-center gap-2 text-sm text-white/90">
                  <span aria-hidden="true">🟢</span>
                  <span className="font-medium">Nur jetzt offene</span>
                </span>
                <span
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    openNowOnly ? 'bg-emerald-500' : 'bg-turbo-600'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      openNowOnly ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </span>
              </button>

              {/* Unter-Schalter „100% sicher" — nur wenn „Nur jetzt offene" an.
                  AN → nur sicher geöffnete (unbekannte auch raus).
                  AUS → unbekannte bleiben drin. */}
              {openNowOnly && (
                <button
                  type="button"
                  onClick={toggleStrict}
                  role="switch"
                  aria-checked={strictOpen}
                  className={`mb-3 -mt-1.5 ml-4 w-[calc(100%-1rem)] flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ${
                    strictOpen
                      ? 'bg-emerald-600/20 border-emerald-400/50'
                      : 'bg-turbo-800/40 border-turbo-700/40'
                  }`}
                >
                  <span className="flex flex-col items-start">
                    <span className="text-[13px] text-white/85 font-medium">100% sicher</span>
                    <span className="text-[10px] text-white/45 leading-tight">
                      {strictOpen ? 'nur sicher geöffnete' : 'auch unbekannte zeigen'}
                    </span>
                  </span>
                  <span
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                      strictOpen ? 'bg-emerald-500' : 'bg-turbo-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        strictOpen ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </span>
                </button>
              )}

              {/* Kategorien */}
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1.5">Was brauchst du?</div>
              <div className="grid grid-cols-2 gap-1.5">
                {(Object.keys(POI_CATEGORIES) as PoiCategoryId[]).map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => findCategory(cat)}
                    className={`flex items-center gap-1.5 rounded-lg px-2 py-2 text-sm border text-left ${
                      activeCat === cat
                        ? 'bg-teal-600/25 border-teal-400/60 text-white'
                        : 'bg-turbo-700/40 border-transparent text-white/85'
                    }`}
                  >
                    <span>{POI_CATEGORIES[cat].emoji}</span>
                    <span className="truncate">{POI_CATEGORIES[cat].label}</span>
                  </button>
                ))}
              </div>

              {/* Ergebnisse */}
              <div className="mt-3">
                {busy && <div className="text-white/60 text-sm">Suche voraus…</div>}
                {error && <div className="text-amber-300 text-[13px] leading-snug">{error}</div>}
                {!busy && results && results.length === 0 && !error && (
                  <div className="text-white/60 text-sm">
                    {openNowOnly && strictOpen
                      ? 'Nichts garantiert offen voraus. „100% sicher" ausschalten, um auch unbekannte zu sehen.'
                      : openNowOnly
                        ? 'Nichts sicher Offenes voraus. „Nur jetzt offene" ausschalten, um alle zu sehen.'
                        : 'Nichts voraus in Reichweite. Probier eine andere Richtung im Test-Modus.'}
                  </div>
                )}
                {!busy && results && results.length > 0 && sourceUsed && (
                  <div className="text-[11px] text-white/45 mb-1.5">
                    {sourceUsed === 'google'
                      ? '🟢 Live von Google'
                      : '🗺️ Offline (OSM)'}
                  </div>
                )}
                {!busy && results && results.length > 0 && (
                  <div className="space-y-1.5">
                    {results.map((c) => (
                      <button
                        key={c.poi.id}
                        type="button"
                        onClick={() => routeTo(c)}
                        className="w-full text-left rounded-lg border border-sky-500/30 bg-turbo-700/40 hover:bg-turbo-700/60 p-2.5"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-[14px] text-white truncate">
                            {activeCat ? POI_CATEGORIES[activeCat].emoji : '📍'}{' '}
                            {c.poi.name || POI_CATEGORIES[activeCat as PoiCategoryId].label}
                          </span>
                          <span className="text-sky-300 text-[13px] tabular-nums shrink-0">
                            {(c.distM / 1000).toFixed(1)} km {compassLabel(c.bearingDeg)}
                          </span>
                        </div>
                        {(() => {
                          const st = poiOpenState(c.poi);
                          if (st === 'open')
                            return (
                              <div className="text-emerald-300 text-[11px] mt-0.5 font-medium">🟢 Jetzt geöffnet</div>
                            );
                          if (st === 'closed')
                            return (
                              <div className="text-red-300 text-[11px] mt-0.5 font-medium">🔴 Jetzt geschlossen</div>
                            );
                          return (
                            <div className="text-white/40 text-[11px] mt-0.5">⚪ Öffnungszeiten unbekannt</div>
                          );
                        })()}
                        <div className="text-white/45 text-[11px] mt-0.5">Tippen → Route dorthin (voraus)</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
